import { isSameDay } from 'date-fns';
import {
  convertDateByTimezone,
  formatToDate,
  formatToDateTimeShort,
  lodash,
} from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Input } from 'libs/ui';

import { Field, useFormContext } from '../../../forms/orderForm';
import { OrderSection } from '../../OrderSection';

import { useExecutionPeriodSection } from './useExecutionPeriodSection';

export interface ExecutionPeriodSectionProps {
  signedAt?: {
    show: boolean;
    value?: Date;
  };

  closedDate?: {
    show: boolean;
    value?: Date;
  };
}

export const ExecutionPeriodSection = ({
  signedAt,
  closedDate,
}: ExecutionPeriodSectionProps) => {
  const { t } = useTranslation();
  const { values, readOnly } = useFormContext();

  const {
    enabledYears,
    disabledDates,
    minEndDate,
    brokerTimezoneOffset,
    onChangeEndDate,
  } = useExecutionPeriodSection();

  return (
    <OrderSection
      fields={lodash.compact([
        signedAt?.show && (
          <Input
            fullWidth
            readOnly
            disabled={!readOnly}
            label={t(
              'account.order.sections.executionPeriodSection.fields.signedAt.label'
            )}
            value={
              signedAt?.value
                ? formatToDateTimeShort(
                    convertDateByTimezone(signedAt.value, brokerTimezoneOffset)
                  )
                : undefined
            }
          />
        ),
        <Field.DatePicker
          fullWidth
          disabled={!readOnly}
          label={t(
            'account.order.sections.executionPeriodSection.fields.startDate.label'
          )}
          name="startDate"
        />,
        <Field.DatePicker
          fullWidth
          showRequiredMark
          disableFuture={false}
          label={t(
            'account.order.sections.executionPeriodSection.fields.endDate.label'
          )}
          minDate={minEndDate ?? values.startDate}
          name="endDate"
          placeholder={t(
            'account.order.sections.executionPeriodSection.fields.endDate.placeholder'
          )}
          shouldDisableDate={(date) =>
            disabledDates.some((d) => isSameDay(date, d))
          }
          shouldDisableYear={(year) => !enabledYears.has(year.getFullYear())}
          onChange={onChangeEndDate}
        />,
        <Field.Quantity
          fullWidth
          disabled={!readOnly}
          label={t(
            'account.order.sections.executionPeriodSection.fields.executionPeriodInBusinessDays.label'
          )}
          name="executionPeriodInBusinessDays"
        />,
        closedDate?.show && (
          <Input
            fullWidth
            readOnly
            disabled={!readOnly}
            label={t(
              'account.order.sections.executionPeriodSection.fields.closedDate.label'
            )}
            value={
              closedDate?.value
                ? formatToDate(
                    convertDateByTimezone(
                      closedDate.value,
                      brokerTimezoneOffset
                    )
                  )
                : undefined
            }
          />
        ),
      ])}
      title={t('account.order.sections.executionPeriodSection.title')}
      titleHint={{
        title: t('account.order.sections.executionPeriodSection.hint.title'),
      }}
    />
  );
};
